import { useQuery, type UseQueryResult } from '@tanstack/react-query'

import {
  BROKER_HIGHLIGHTS_AVAILABLE,
  brokerHighlightsService,
  type BrokerHighlight,
} from '@/features/broker-rail/api/highlightsService'
import { useBrokerList } from '@/features/brokers/api/queries'
import { BROKER_DIRECTORY_QUERY } from '@/features/brokers/lib/brokerDirectory'
import { cmp } from '@/lib/money'
import type { BrokerListRow } from '@/schemas/broker'

/** Its own root, as the Market rail's is: a page's refresh must not re-fetch the rail. */
export const brokerRailKeys = {
  all: ['broker-rail'] as const,
  highlights: () => [...brokerRailKeys.all, 'highlights'] as const,
}

/** Never runs while `BROKER_HIGHLIGHTS_AVAILABLE` is false. */
export const useBrokerHighlights = (): UseQueryResult<BrokerHighlight[]> =>
  useQuery({
    queryKey: brokerRailKeys.highlights(),
    queryFn: () => brokerHighlightsService.today(),
    enabled: BROKER_HIGHLIGHTS_AVAILABLE,
  })

/** The mock draws five. */
export const TOP_RATED_LIMIT = 5

/**
 * The highest-rated houses, best first: the broker directory (`/brokers/`, every house in one
 * page, the same cache entry the pickers read) sorted by rating on the client, since the endpoint
 * has no `sort=rating` yet (asked in asks/broker-rail.md). Unrated houses are left out — a house
 * nobody has reviewed is not "rated" at all. Ties go to the busier house, then the lower number.
 */
export const topRatedBrokers = (rows: readonly BrokerListRow[]): BrokerListRow[] =>
  rows
    .flatMap((row) => (row.rating === null ? [] : [{ row, rating: row.rating }]))
    .sort(
      (a, b) =>
        cmp(b.rating, a.rating) ||
        cmp(b.row.turnover, a.row.turnover) ||
        a.row.code.localeCompare(b.row.code, 'en', { numeric: true }),
    )
    .slice(0, TOP_RATED_LIMIT)
    .map(({ row }) => row)

/** The directory read, for the Top rated block. */
export const useBrokerDirectory = () => useBrokerList(BROKER_DIRECTORY_QUERY)
